/**
 * SB2-CC-01 — TanStack Query hook for `GET /api/auth/v1/role-matrix`.
 *
 * The matrix is **static across users** within a deploy — every caller
 * in the same platform receives the same body. We cache it under the
 * JWT's `jti` so a forced rotation invalidates it and a hot-reload of
 * the gateway picks up the new matrix on the next refetch.
 *
 * The endpoint requires authentication but no special role; it is
 * effectively a read-side dependency of every gated UI. We fail
 * **soft**: if the request errors we keep the previous matrix in the
 * cache. If there is no previous matrix the consumer sees
 * `isLoading === true` indefinitely (until a retry succeeds) — the
 * downstream `useCanEditResource` hook returns `false` in that state,
 * so the UI stays read-only, which is the correct safe default.
 *
 * **SB4-07 fix.** We deliberately do NOT use `useApi()` (the lyse-base
 * axios instance) here. `useApi`'s `baseURL` defaults to `/api/lyse/v1`,
 * so `api.get('/api/auth/v1/role-matrix')` resolved to
 * `/api/lyse/v1/api/auth/v1/role-matrix` (doubled path, 404). The
 * matrix never loaded; `useCanEditResource` returned `false`; every
 * Add/Edit gate on assets surfaces stayed read-only even for users who
 * actually had the permission. Switching to a direct `fetch` against
 * `getAuthBaseUrl()` is the same pattern apps/web's `authFetch` uses
 * for the other `/api/auth/v1/*` calls (Users / Invitations / Audit).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { AeroApiError } from "@aeriosiq/api-client";
import { useAuthStore } from "./store.js";
import { getAuthBaseUrl } from "./config.js";
import { fetchWithRefresh } from "./fetchWithRefresh.js";
import type { AuthComponents } from "@aeriosiq/types";
import { useMemo } from "react";

export type RoleMatrix = AuthComponents["schemas"]["RoleMatrix"];

/**
 * Decode the JWT `jti` (or fall back to a hash of the access token) so
 * the TanStack query key changes on token rotation. We don't validate
 * the JWT — just extract the `jti` claim from the payload segment. If
 * decoding fails we use a stable fallback so the query still has a
 * cache key.
 */
function jtiFromAccessToken(token: string | null): string {
  if (!token) return "no-token";
  const parts = token.split(".");
  if (parts.length < 2) return token.slice(0, 16);
  try {
    // JWT payload is base64url-encoded JSON. Normalise to base64.
    const payload = parts[1] ?? "";
    const normalised = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(normalised + "===".slice((normalised.length + 3) % 4));
    const parsed = JSON.parse(decoded) as { jti?: string; sub?: string };
    return parsed.jti ?? parsed.sub ?? token.slice(0, 16);
  } catch {
    return token.slice(0, 16);
  }
}

export function useRoleMatrix(): UseQueryResult<RoleMatrix, AeroApiError> {
  const accessToken = useAuthStore((s) => s.session?.accessToken ?? null);
  const tenantSlug = useAuthStore((s) => s.tenant?.tenantSlug ?? null);
  const jti = useMemo(() => jtiFromAccessToken(accessToken), [accessToken]);

  return useQuery<RoleMatrix, AeroApiError>({
    queryKey: ["auth", "role-matrix", jti],
    queryFn: async () => {
      const url = `${getAuthBaseUrl()}/role-matrix`;
      const headers = new Headers();
      if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
      if (tenantSlug) headers.set("X-Tenant-ID", tenantSlug);
      // SB6-AUTH-REFRESH-01 — raw-fetch wrapper that exchanges the
      // refresh token on 401 and replays once with the new access
      // token. Prevents the role-matrix query from returning a 401
      // forever after an access-token expiry (which would silently
      // collapse every gated UI to read-only until the user
      // re-loaded the page).
      const res = await fetchWithRefresh(url, { method: "GET", headers });
      if (!res.ok) {
        const err = new Error(
          `role-matrix fetch failed: ${String(res.status)}`,
        ) as AeroApiError;
        err.name = "AeroApiError";
        err.errorId = "role-matrix-fetch-failed";
        err.status = res.status;
        throw err;
      }
      return (await res.json()) as RoleMatrix;
    },
    enabled: accessToken !== null,
    // Matrix is static within a deploy; cache aggressively. A hot-reload
    // of the gateway + a JWT rotation are the only invalidators.
    staleTime: 30 * 60_000, // 30 min
    gcTime: 60 * 60_000, // 1 h
    retry: 1,
  });
}
